import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormSubmit } from '@/hook/useFormSubmit';
import { handleErrorResponse } from '@/utils/handleErrorResponse';
import api from '@/data/api';
import toast from 'react-hot-toast';
import { Button, Container, TextField, Grid, Typography } from '@mui/material';
import { DatePicker, ImageUploader, LineBreaker } from '@/atomicComponent/AtomicComponents';
import { FormControlLabel, Checkbox } from '@mui/material';
import Toast from '@/utils/toastManager';

const DEVICE_DESKTOP = 'desktop';
const DEVICE_MOBILE = 'mobile';
// TODO: decide what to do here, currently default to desktop only to prevent popups being visible on mobile
const DEFAULT_DEVICE_VISIBILITY = [DEVICE_DESKTOP];

export const ProducePopups = ({ initialValues, onSubmit }) => {
	const navigate = useNavigate();

	const [popupDetails, setPopupDetails] = React.useState({
		headerText: '',
		description: '',
		image: null,
		isActive: false,
		startDate: new Date(),
		endDate: new Date(),
		primaryCTA: { text: '', redirectUrl: '' },
		secondaryCTA: { text: '', redirectUrl: '' },
		viewCount: 0,
		startDateLocal: '',
		endDateLocal: '',
		deviceVisibility: DEFAULT_DEVICE_VISIBILITY
	});

	useEffect(() => {
		if (initialValues) {
			const normalized = { ...initialValues };
			if (Array.isArray(initialValues.deviceVisibility)) {
				normalized.deviceVisibility = initialValues.deviceVisibility.map(d => String(d).toLowerCase());
			}
			setPopupDetails(prev => ({
				...prev,
				...normalized,
				startDate: new Date(initialValues.startDateLocal),
				endDate: new Date(initialValues.endDateLocal)
			}));
		}
	}, [initialValues]);

	const handleChange = prop => event => {
		const value = event?.target ? event.target.value : event;

		setPopupDetails(prev => {
			const updated = { ...prev, [prop]: value };

			if (prop === 'startDate' || prop === 'endDate') {
				const dateObj = new Date(value);
				const year = dateObj.getFullYear();
				const month = String(dateObj.getMonth() + 1).padStart(2, '0');
				const day = String(dateObj.getDate()).padStart(2, '0');
				const localDateStr = `${year}-${month}-${day} 00:00:00`;

				updated[`${prop}Local`] = localDateStr;
			}

			return updated;
		});
	};

	const handleCTAChange = (type, field) => event => {
		const value = event?.target ? event.target.value : event;
		setPopupDetails(prev => ({
			...prev,
			[type]: {
				...prev[type],
				[field]: value
			}
		}));
	};

	const handleDeviceVisibilityChange = device => event => {
		const checked = event?.target ? event.target.checked : !!event;
		setPopupDetails(prev => {
			const current = Array.isArray(prev.deviceVisibility)
				? prev.deviceVisibility.map(d => String(d).toLowerCase())
				: [];
			if (checked) {
				// add if not present
				if (!current.includes(device)) {
					return { ...prev, deviceVisibility: [...current, device] };
				}
				return prev;
			} else {
				// remove if present
				return { ...prev, deviceVisibility: current.filter(d => d !== device) };
			}
		});
	};

	// compute selected devices count for validation
	const selectedDevicesCount = Array.isArray(popupDetails.deviceVisibility)
		? popupDetails.deviceVisibility.filter(d => !!String(d).trim()).length
		: 0;

	const handleSubmit = useFormSubmit(async token => {
		// validation: require at least one device
		if (selectedDevicesCount === 0) {
			toast.error('Please select at least one device (Desktop or Mobile).');
			return;
		}
		let submitImage = popupDetails.image;
		if (popupDetails.image instanceof File) {
			const imageUrl = await api.fileUploadApi(popupDetails.image);
			if (handleErrorResponse(imageUrl)) return;
			submitImage = imageUrl?.data?.cdnLink;
		}

		const formatLocalDateTime = (date, hours, minutes, seconds) => {
			const newDate = new Date(date);
			newDate.setHours(hours, minutes, seconds, 0);

			const year = newDate.getFullYear();
			const month = String(newDate.getMonth() + 1).padStart(2, '0');
			const day = String(newDate.getDate()).padStart(2, '0');
			const hour = String(newDate.getHours()).padStart(2, '0');
			const minute = String(newDate.getMinutes()).padStart(2, '0');
			const second = String(newDate.getSeconds()).padStart(2, '0');

			return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
		};

		const payload = {
			headerText: popupDetails.headerText,
			thumbnailLink: submitImage,
			description: popupDetails.description,
			startDateLocal: formatLocalDateTime(popupDetails.startDate, 0, 0, 0),
			startDate: formatLocalDateTime(popupDetails.startDate, 4, 0, 0),
			endDate: formatLocalDateTime(popupDetails.endDate, 3, 59, 0),
			endDateLocal: formatLocalDateTime(popupDetails.endDate, 23, 59, 0),
			isActive: popupDetails.isActive,
			primaryCTA: popupDetails.primaryCTA,
			secondaryCTA: popupDetails.secondaryCTA,
			viewCount: parseInt(popupDetails.viewCount) ?? 0,
			deviceVisibility: Array.isArray(popupDetails.deviceVisibility) ? popupDetails.deviceVisibility : [],
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
		};
		try {
			const response = await onSubmit(payload, token);
			Toast.success(response.message);
			resetForm();
		} catch (error) {
			Toast.error(error?.response?.data?.message || 'An error occurred while submitting the popup.');
		}
	});

	const resetForm = () => {
		setPopupDetails({
			headerText: '',
			description: '',
			image: null,
			isActive: false,
			startDate: new Date(),
			endDate: new Date(),
			primaryCTA: { text: '', redirectUrl: '' },
			secondaryCTA: { text: '', redirectUrl: '' },
			viewCount: 0,
			deviceVisibility: DEFAULT_DEVICE_VISIBILITY
		});
		navigate('/AdminDashboard/Popup');
	};

	return (
		<Container maxWidth="lg">
			<form>
				<TextField
					variant="outlined"
					label="Header Text"
					fullWidth
					value={popupDetails.headerText}
					onChange={handleChange('headerText')}
				/>
				<LineBreaker />
				<TextField
					variant="outlined"
					label="Description"
					fullWidth
					multiline
					rows={3}
					value={popupDetails.description}
					onChange={handleChange('description')}
				/>
				<LineBreaker />
				<ImageUploader label="Upload Thumbnail" value={popupDetails.image} handleImageChange={handleChange('image')} />
				<LineBreaker />
				<Grid container spacing={2}>
					<Grid item xs={6}>
						<DatePicker
							label="Start Date"
							value={popupDetails.startDate}
							handleDateChange={handleChange('startDate')}
						/>
					</Grid>
					<Grid item xs={6}>
						<DatePicker label="End Date" value={popupDetails.endDate} handleDateChange={handleChange('endDate')} />
					</Grid>
					<Grid item xs={12}>
						<Typography variant="subtitle1">Device Visibility</Typography>
						<FormControlLabel
							control={
								<Checkbox
									checked={popupDetails.deviceVisibility?.map(d => String(d).toLowerCase()).includes(DEVICE_DESKTOP)}
									onChange={handleDeviceVisibilityChange(DEVICE_DESKTOP)}
								/>
							}
							label="Desktop"
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={popupDetails.deviceVisibility?.map(d => String(d).toLowerCase()).includes(DEVICE_MOBILE)}
									onChange={handleDeviceVisibilityChange(DEVICE_MOBILE)}
								/>
							}
							label="Mobile"
						/>
						{selectedDevicesCount === 0 && (
							<Typography variant="caption" color="error" display="block">
								Please select at least one device (Desktop or Mobile).
							</Typography>
						)}
					</Grid>
				</Grid>
				<LineBreaker />
				<TextField
					variant="outlined"
					label="Primary CTA Text"
					fullWidth
					value={popupDetails.primaryCTA.text}
					onChange={handleCTAChange('primaryCTA', 'text')}
				/>
				<LineBreaker />
				<TextField
					variant="outlined"
					label="Primary CTA Redirect URL"
					fullWidth
					value={popupDetails.primaryCTA.redirectUrl}
					onChange={handleCTAChange('primaryCTA', 'redirectUrl')}
				/>
				<LineBreaker />
				<TextField
					variant="outlined"
					label="Secondary CTA Text"
					fullWidth
					value={popupDetails.secondaryCTA.text}
					onChange={handleCTAChange('secondaryCTA', 'text')}
				/>
				<LineBreaker />
				<TextField
					variant="outlined"
					label="Secondary CTA Redirect URL"
					fullWidth
					value={popupDetails.secondaryCTA.redirectUrl}
					onChange={handleCTAChange('secondaryCTA', 'redirectUrl')}
				/>
				<LineBreaker />

				<TextField
					variant="outlined"
					label="View Count"
					type="number"
					fullWidth
					value={popupDetails.viewCount}
					onChange={handleChange('viewCount')}
				/>
				<LineBreaker />
				<Button
					sx={{ backgroundColor: '#7367f0' }}
					variant="contained"
					color="secondary"
					type="button"
					onClick={handleSubmit}
				>
					Submit
				</Button>
			</form>
		</Container>
	);
};
